/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import Text from "../Text";
import View from "../Container";
import useAuthHttp from "../../hooks/useAuthHttp";
import { IAssetDetailsResponse, IAssetInfo, IAssetPrivateBondsInsert, IAssetTypesResponse } from "../../models/asset";
import { groupAssetListTypeByGroupName } from "../../commons/group-assets-list-type";
import Button from "../Button";
import Nav, { NavItem } from "../Nav";
import ButtonGroup from "../ButtonGroup";
import FormField from "../FormField";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import BankSearchSelector from "../BankSearchSelector";
import { Toast } from "primereact/toast";
import { useHomeContext } from "../../contexts/HomeContext";
import Label from "../Label";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import Container from "../Container";
import { IExtractedInvestment } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";

export interface IPrivateBondsHandleFormProps {
    onSuccessRegister(assetInfo: IPrivateBondsHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedInvestment;
}

export interface IPrivateBondsHandleForm {
    group?: { id: string; name: string; };
    dueDate?: Date;
    purchaseDate?: Date;
    isPre?: boolean;
    taxName?: string;
    taxValue?: number;
    isYearTax?: boolean;
    yearTax?: number;
    issuingBank?: {
        id: string;
        name: string;
    };
    negotiatingBank?: {
        id: string;
        name: string;
    };
    operationCosts?: {
        brokerage?: number;
        others?: number;
    };
    investedValue?: number;
}

const PrivateBondsHandleForm = (props: IPrivateBondsHandleFormProps) => {
    const [isEdit, setIsEdit] = useState(false);
    const toast = useRef<Toast | null>(null);
    const formik = useFormik<IPrivateBondsHandleForm>({
        initialValues: {
            group: {
                id: "",
                name: ""
            },
            dueDate: undefined,
            purchaseDate: undefined,
            isPre: false,
            taxName: undefined,
            taxValue: 0,
            isYearTax: false,
            yearTax: 0,
            issuingBank: undefined,
            negotiatingBank: undefined,
            operationCosts: {
                brokerage: 0,
                others: 0,
            },
            investedValue: 0,
        },
        validationSchema: Yup.object().shape({
            group: Yup
                .object()
                .shape(
                    {
                        id: Yup
                            .string()
                            .required("Campo obrigatório")
                    }
                ),
            dueDate: Yup
                .date()
                .required("Campo obrigatório"),
            purchaseDate: Yup
                .date()
                .required("Campo obrigatório"),
            isPre: Yup
                .boolean()
                .optional(),
            taxName: Yup
                .string()
                .optional(),
            taxValue: Yup
                .number()
                .nullable()
                .when("isPre", {
                    is: false,
                    then: (schema) => schema.required("Campo obrigatório").min(0.1, "É obrigatório informar um valor"),
                    otherwise: (schema) => schema.optional()
                }),
            isYearTax: Yup
                .boolean()
                .when("isPre", {
                    is: true,
                    then: (schema) => schema.required("Campo obrigatório"),
                    otherwise: (schema) => schema.optional()
                }),
            yearTax: Yup
                .number()
                .nullable()
                .when("isYearTax", {
                    is: true,
                    then: (schema) => schema.required("Campo obrigatório").min(0.1, "É obrigatório informar um valor anual"),
                    otherwise: (schema) => schema.optional()
                }),
            issuingBank: Yup
                .object()
                .required("Campo obrigatório"),
            negotiatingBank: Yup
                .object()
                .required("Campo obrigatório"),
            operationCosts: Yup
                .object()
                .shape(
                    {
                        others: Yup
                            .number()
                            .nullable()
                            .optional(),
                        brokerage: Yup
                            .number()
                            .nullable()
                            .optional()
                    }
                ),
            investedValue: Yup
                .number()
                .min(10, "Valor mínimo obrigatório")
                .required("Campo obrigatório")
        }),
        onSubmit: (values) => send(values)
    });
    const http = useAuthHttp();
    const homeContext = useHomeContext();

    const insertAssetMutation = useMutation({
        mutationFn: async (assetPayload: IAssetPrivateBondsInsert) => {
            const body = {
                asset_id: assetPayload.assetGroupId,
                asset_type: "PB",
                asset_due_date: moment(assetPayload.dueDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_sub_type: assetPayload.subType,
                asset_fin_negotiating_id: assetPayload.negotiatingId,
                asset_fin_issue_id: assetPayload.issuingId,
                asset_purchase_date: moment(assetPayload.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_pb_details: {
                    pb_type: assetPayload.type,
                    pb_rate_cdi: assetPayload.cdiTaxRate,
                    pb_rate_pos_plus_cdi: assetPayload.cdiPosTaxRate,
                    pb_rate_pos_plus_ipca: assetPayload.ipcaPosTaxRate,
                    pb_rate_pos_plus_igpm: assetPayload.igpmPosTaxRate,
                    pb_rate_pre_annual: assetPayload.yearTaxRate,
                    pb_oc_brokerage: assetPayload.brokerageValue,
                    pb_oc_others: assetPayload.othersValue
                },
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(assetPayload.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: assetPayload.investedValue
                },
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {})
            };

            return await http.doPost<any, IAssetInfo>("/asset/insert", body);
        }
    });

    const updateAssetMutation = useMutation({
        mutationFn: async (assetPayload: IAssetPrivateBondsInsert) => {
            const body = {
                asset_due_date: moment(assetPayload.dueDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_fin_negotiating_id: assetPayload.negotiatingId,
                asset_fin_issue_id: assetPayload.issuingId,
                asset_purchase_date: moment(assetPayload.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_pb_details: {
                    pb_type: assetPayload.type,
                    pb_rate_cdi: assetPayload.cdiTaxRate,
                    pb_rate_pos_plus_cdi: assetPayload.cdiPosTaxRate,
                    pb_rate_pos_plus_ipca: assetPayload.ipcaPosTaxRate,
                    pb_rate_pos_plus_igpm: assetPayload.igpmPosTaxRate,
                    pb_rate_pre_annual: assetPayload.yearTaxRate,
                    pb_oc_brokerage: assetPayload.brokerageValue,
                    pb_oc_others: assetPayload.othersValue
                },
                asset_event: {
                    event_type: "CHARGE",
                    event_date: moment(assetPayload.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                    event_value: assetPayload.investedValue
                }
            };

            return await http.doPatch<any, IAssetInfo>(`/asset/${props.assetId}`, body);
        }
    });

    const assetListTypeQuery = useQuery({
        queryKey: [`assetListType`],
        queryFn: async () =>
            await http.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGrouped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                return {
                    asset_info: null
                };
            }

            return await http.doGet<IAssetDetailsResponse>(`/asset/${props.assetId}`)
        }
    });

    useEffect(() => {
        if (!assetDetailsQuery.data?.asset_info) {
            setIsEdit(false);
            return;
        }

        setIsEdit(true);
        const taxName = assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_cdi
            ? "CDI"
            : assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_ipca
                ? "IPCA"
                : assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_igpm
                    ? "IGPM"
                    : "";
        const taxValue = assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_cdi
            ? assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_cdi
            : assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_ipca
                ? assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_ipca
                : assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_igpm
                    ? assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pos_plus_igpm
                    : assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_cdi;
        const eventCharge = assetDetailsQuery.data?.asset_info.asset_event_user.find(e => e.event_type == "CHARGE");
        formik.setValues({
            group: assetListTypeGrouped
                .find(listType => listType.groupName == "CDB,CRA,CRI,LC,LCA,LCI,LF")
                ?.names
                .find(groupName => groupName.id === assetDetailsQuery.data?.asset_info?.asset_id),
            dueDate: moment(assetDetailsQuery.data?.asset_info?.asset_due_date).toDate(),
            purchaseDate: moment(assetDetailsQuery.data?.asset_info?.asset_purchase_date).toDate(),
            isPre: !!assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pre_annual,
            taxName,
            taxValue: !taxValue ? undefined : taxValue,
            isYearTax: !!assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pre_annual,
            yearTax: assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_rate_pre_annual,
            issuingBank: {
                id: assetDetailsQuery.data?.asset_info?.asset_fin_issue.id,
                name: assetDetailsQuery.data?.asset_info?.asset_fin_issue.inst_name
            },
            negotiatingBank: {
                id: assetDetailsQuery.data?.asset_info?.asset_fin_negotiating.id,
                name: assetDetailsQuery.data?.asset_info?.asset_fin_negotiating.inst_name
            },
            operationCosts: {
                brokerage: assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_oc_brokerage,
                others: assetDetailsQuery.data?.asset_info?.asset_pb_details.pb_oc_others,
            },
            investedValue: eventCharge?.event_value
        });
    }, [assetDetailsQuery.data?.asset_info, assetListTypeGrouped.length]);

    useEffect(() => {
        if (!props.initialData?.asset_pb_details) {
            return;
        }
        const data = props.initialData;
        const pbType = (data.asset_pb_details?.pb_type || data.asset_sub_type || "").trim();
        const group =
            assetListTypeGrouped.find((g) => g.groupName === "CDB,CRA,CRI,LC,LCA,LCI,LF") ??
            assetListTypeGrouped.find(
                (g) =>
                    g.groupName.includes("CDB") ||
                    g.groupName.includes("LCI") ||
                    g.groupName.includes("LCA")
            );

        const matchName = (a: string, b: string) =>
            a.length > 0 && b.length > 0 && a.toLowerCase() === b.toLowerCase();

        const matchedAssetType =
            group?.names.find((n) => matchName(n.name, pbType)) ??
            group?.names.find((n) => matchName(n.name, data.asset_sub_type || ""));

        const groupFormValue =
            group && matchedAssetType
                ? { id: matchedAssetType.id, name: matchedAssetType.name }
                : undefined;

        formik.setValues({
            group: groupFormValue,
            purchaseDate: data.asset_purchase_date ? moment(data.asset_purchase_date).toDate() : undefined,
            dueDate: data.asset_due_date ? moment(data.asset_due_date).toDate() : undefined,
            isPre: (data.asset_pb_details?.pb_rate_pre_annual || 0) > 0,
            taxName: data.asset_pb_details?.pb_rate_cdi ? "CDI" : undefined,
            taxValue: data.asset_pb_details?.pb_rate_cdi || 0,
            yearTax: data.asset_pb_details?.pb_rate_pre_annual || 0,
            isYearTax: (data.asset_pb_details?.pb_rate_pre_annual || 0) > 0,
            investedValue: data.asset_current_value || 0,
            issuingBank: data.asset_fin_issue ? {
                id: data.asset_fin_issue.id,
                name: data.asset_fin_issue.inst_name
            } : undefined,
            negotiatingBank: data.asset_fin_negotiating ? {
                id: data.asset_fin_negotiating.id,
                name: data.asset_fin_negotiating.inst_name
            } : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData, assetListTypeGrouped.length]);

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    const upsert = async (assetPayload: IAssetPrivateBondsInsert) => {
        if (isEdit) return await updateAssetMutation.mutateAsync(assetPayload);

        return await insertAssetMutation.mutateAsync(assetPayload);
    }

    const send = async (form: IPrivateBondsHandleForm) => {
        const assetPayload = {
            assetGroupId: form.group!.id,
            dueDate: form!.dueDate!,
            purchaseDate: form!.purchaseDate!,
            negotiatingId: form!.negotiatingBank!.id,
            issuingId: form!.issuingBank!.id,
            subType: form.taxName ? form.taxName : "CDI",
            type: form!.group!.name,
            cdiTaxRate: !form.taxName ? +form.taxValue! : null,
            cdiPosTaxRate: form!.taxName == "CDI" ? +form.taxValue! : null,
            ipcaPosTaxRate: form!.taxName == "IPCA" ? +form.taxValue! : null,
            igpmPosTaxRate: form!.taxName == "IGPM" ? +form.taxValue! : null,
            yearTaxRate: form?.yearTax ? +form.yearTax! : null,
            brokerageValue: form?.operationCosts?.brokerage,
            othersValue: form?.operationCosts?.others,
            investedValue: form?.investedValue
        };

        try {
            const assetInfo = await upsert(assetPayload);

            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={assetDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!assetDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Título privado</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <View flexColumn>
                        <Text>
                            Escolha um ativo
                        </Text>

                        <View
                            className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                        >
                            <ButtonGroup
                                invalid={!!formik.errors.group && formik.touched.group}
                                group={assetListTypeGrouped
                                    .find(listType =>
                                        listType.groupName == "CDB,CRA,CRI,LC,LCA,LCI,LF"
                                    )?.names
                                    .map(g => (
                                        {
                                            name: g.name,
                                            disabled: isEdit,
                                            selected: formik.values.group?.id == g.id,
                                            onClick: () => formik.setFieldValue("group", g)
                                        })) ?? []
                                }
                            />
                        </View>

                        {
                            !!formik.errors.group && formik.touched.group && (
                                <Text className="text-red-500 mt-2" size="xs">{(formik.errors.group as any)?.id}</Text>
                            )
                        }
                    </View>

                    <FormField
                        id="dueDate"
                        label="Qual o vencimento?"
                        hasError={!!formik.errors.dueDate && formik.touched.dueDate}
                        error={formik.errors.dueDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.dueDate && formik.touched.dueDate}
                            value={formik.values.dueDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            showIcon
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("dueDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <View flexColumn>
                        <Text>
                            Qual o tipo?
                        </Text>

                        <View
                            className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                        >
                            <ButtonGroup
                                invalid={!!formik.values.isPre && formik.touched.isPre}
                                group={[
                                    {
                                        name: "Pós-fixado",
                                        disabled: isEdit,
                                        selected: !formik.values.isPre,
                                        onClick: () => {
                                            formik.setFieldValue("isPre", false);
                                            formik.setFieldValue("isYearTax", false);
                                            formik.setFieldValue("taxName", "");
                                            formik.setFieldValue("taxValue", 0);
                                            formik.setFieldValue("yearTax", 0);
                                        }
                                    },
                                    {
                                        name: "Pré-fixado",
                                        disabled: isEdit,
                                        selected: !!formik.values.isPre,
                                        onClick: () => {
                                            formik.setFieldValue("isPre", true);
                                            formik.setFieldValue("isYearTax", true);
                                            formik.setFieldValue("taxName", undefined);
                                            formik.setFieldValue("yearTax", 0);
                                            formik.setFieldValue("taxValue", 0);
                                        }
                                    }
                                ]}
                            />
                        </View>

                        {
                            !formik.values.isPre && (
                                <View
                                    className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded ${!!formik.errors.group && formik.touched.group ? "border border-rose-500" : ""}`}
                                >
                                    <ButtonGroup
                                        group={[
                                            {
                                                name: "% do CDI",
                                                disabled: isEdit,
                                                selected: formik.values.taxName == "",
                                                onClick: () => {
                                                    formik.setFieldValue("taxName", "");
                                                    formik.setFieldValue("isYearTax", false);
                                                }
                                            },
                                            {
                                                name: "CDI+",
                                                disabled: isEdit,
                                                selected: formik.values.taxName == "CDI",
                                                onClick: () => {
                                                    formik.setFieldValue("taxName", "CDI");
                                                    formik.setFieldValue("isYearTax", false);
                                                }
                                            },
                                            {
                                                name: "IPCA+",
                                                disabled: isEdit,
                                                selected: formik.values.taxName == "IPCA",
                                                onClick: () => {
                                                    formik.setFieldValue("taxName", "IPCA");
                                                    formik.setFieldValue("isYearTax", false);
                                                }
                                            },
                                            {
                                                name: "IGPM+",
                                                disabled: isEdit,
                                                selected: formik.values.taxName == "IGPM",
                                                onClick: () => {
                                                    formik.setFieldValue("taxName", "IGPM");
                                                    formik.setFieldValue("isYearTax", false);
                                                }
                                            }
                                        ]}
                                    />

                                    <InputNumber
                                        className="w-full"
                                        invalid={!!formik.errors.taxValue && formik.touched.taxValue}
                                        value={formik.values.taxValue}
                                        onValueChange={(e) => {
                                            formik.setFieldValue("taxValue", e.value);
                                            formik.setFieldValue("yearTax", undefined);
                                        }}
                                        prefix={`${formik.values.taxName != undefined && formik.values.taxName != "" ? `${formik.values.taxName} + ` : ""}`}
                                        suffix={`${formik.values.taxName == "" ? "% do CDI" : "%"}`}
                                        maxFractionDigits={2}
                                        min={0}
                                        max={200}
                                    />

                                    {
                                        !!formik.errors.taxValue && formik.touched.taxValue && (
                                            <Text className="text-red-500 mt-2" size="xs">{formik.errors.taxValue}</Text>
                                        )
                                    }
                                </View>
                            )
                        }

                        {
                            !!formik.values.isPre
                            && (
                                <View
                                    className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded ${!!formik.errors.group && formik.touched.group ? "border border-rose-500" : ""}`}
                                >
                                    <InputNumber
                                        className="w-full"
                                        invalid={!!formik.errors.yearTax && formik.touched.yearTax}
                                        value={formik.values.yearTax}
                                        onValueChange={(e) => {
                                            formik.setFieldValue("taxValue", undefined);
                                            formik.setFieldValue("yearTax", e.value);
                                        }}
                                        prefix="Taxa anual + "
                                        suffix=" %"
                                        maxFractionDigits={2}
                                        min={0}
                                        max={200}
                                    />

                                    {
                                        !!formik.errors.yearTax && formik.touched.yearTax && (
                                            <Text className="text-red-500 mt-2" size="xs">{formik.errors.yearTax}</Text>
                                        )
                                    }
                                </View>
                            )
                        }
                    </View>

                    <BankSearchSelector
                        hasError={!!formik.errors.issuingBank && !!formik.touched.issuingBank}
                        error={formik.errors.issuingBank}
                        selected={formik.values.issuingBank}
                        label="Qual é o banco emissor?"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("issuingBank", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <BankSearchSelector
                        hasError={!!formik.errors.negotiatingBank && !!formik.touched.negotiatingBank}
                        error={formik.errors.negotiatingBank}
                        selected={formik.values.negotiatingBank}
                        label="Onde você comprou?"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("negotiatingBank", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <FormField
                        id="purchaseDate"
                        label="Quando você comprou?"
                        hasError={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                        error={formik.errors.purchaseDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                            value={formik.values.purchaseDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            showIcon
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("purchaseDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <View flexColumn gap={2}>
                        <Label>Corretagem</Label>
                        <InputNumber
                            value={formik.values.operationCosts?.brokerage}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, brokerage: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <View flexColumn gap={2}>
                        <Label>Outros custos</Label>
                        <InputNumber
                            value={formik.values.operationCosts?.others}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, others: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <FormField
                        id="investedValue"
                        hasError={!!formik.errors.investedValue && formik.touched.investedValue}
                        error={formik.errors.investedValue}
                        label="Valor investido"
                    >
                        <InputNumber
                            invalid={!!formik.errors.investedValue && formik.touched.investedValue}
                            value={formik.values.investedValue}
                            onValueChange={(e) =>
                                formik.setFieldValue("investedValue", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={insertAssetMutation.isPending || updateAssetMutation.isPending}
                            disabled={insertAssetMutation.isPending || updateAssetMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    );
}

export default PrivateBondsHandleForm;